import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import ApiService from '../services/ApiService';
import { toast } from 'react-toastify';
import LoadingIndicator from '../components/LoadingIndicator';
import { buildChartImageUrl } from '../components/chartImageUrl';
import FinancialHistoryCharts from '../components/FinancialHistoryCharts';
import {
  dcfValuationColorClass,
  formatChartDetailValue,
  pegRatioColorClass,
  priceToDcfRatioColorClass,
} from './chartDetailFormatting';
import { getScreenerOrderedList, parseScreenerNavState } from '../screener/chartNav';

const HoldingChip = ({ symbol }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stockId, setStockId] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkAvailability = async () => {
      try {
        setLoading(true);
        const rec = await ApiService.lookupStockBySymbol(symbol);
        if (cancelled) return;
        setStockId(rec?.id || null);
        setUnavailable(!rec?.id);
      } catch (e) {
        if (cancelled) return;
        setStockId(null);
        setUnavailable(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    checkAvailability();
    return () => { cancelled = true; };
  }, [symbol]);

  const onClick = () => {
    if (!stockId || unavailable) return;
    navigate(`/chart/${stockId}`);
  };

  const unavailableTitle = `${symbol} is not in the current database`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading || unavailable}
      className={`px-3 py-1 rounded-full border text-sm font-medium transition-colors ${
        loading
          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-wait'
          : unavailable
            ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
      }`}
      title={unavailable ? unavailableTitle : `Open ${symbol}`}
    >
      {symbol}
    </button>
  );
};

const ChartDetailView = () => {
  const { stockId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  
  const [currentStock, setCurrentStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [imageCacheKey, setImageCacheKey] = useState('');
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [navigationContext, setNavigationContext] = useState(null);

  useEffect(() => {
    const context = location.state || {
      filters: {},
      page: 1,
      stockIndex: 0,
      totalFiltered: 0
    };
    setNavigationContext(context);
  }, [location.state]);

  useEffect(() => {
    const fetchStockData = async () => {
      try {
        setLoading(true);
        
        const stock = await ApiService.getRecordById(stockId);
        setCurrentStock(stock);
        setImageCacheKey(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
        
        // Screener entry: rebuild its filtered+sorted result list with the
        // same registry the grid used, so Next/Previous walk that subset.
        const screenerNav = parseScreenerNavState(navigationContext?.screener);
        if (screenerNav) {
          const list = await getScreenerOrderedList(screenerNav);
          setFilteredStocks(list);
          // -1 (stock no longer matches) disables Next/Previous.
          setCurrentIndex(list.findIndex(s => String(s.id) === String(stockId)));
        } else if (navigationContext && navigationContext.filters) {
          const allFilteredRecords = await ApiService.getRecords(
            navigationContext.filters, 
            1, 
            10000
          );
          
          setFilteredStocks(allFilteredRecords.data);
          const index = allFilteredRecords.data.findIndex(s => String(s.id) === String(stockId));
          setCurrentIndex(index >= 0 ? index : 0);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching stock data:', error);
        toast.error('Failed to load stock details');
        setLoading(false);
      }
    };

    if (stockId && navigationContext) {
      fetchStockData();
    }
  }, [stockId, navigationContext]);

  const handleImageError = () => { setImageError(true); };

  const getImageUrl = () => {
    if (!currentStock) return null;
    const BASE_IMG_URL = process.env.REACT_APP_CHART_IMAGE_BASE_URL || 'https://www.swingtradertrends.com/rj/img/';
    return buildChartImageUrl(currentStock, BASE_IMG_URL, imageCacheKey);
  };

  const goToPrevious = () => {
    if (currentIndex > 0 && filteredStocks.length > 0) {
      const previousStock = filteredStocks[currentIndex - 1];
      navigate(`/chart/${previousStock.id}`, {
        state: { ...navigationContext, stockIndex: currentIndex - 1 }
      });
    }
  };

  const goToNext = () => {
    if (currentIndex >= 0 && currentIndex < filteredStocks.length - 1 && filteredStocks.length > 0) {
      const nextStock = filteredStocks[currentIndex + 1];
      navigate(`/chart/${nextStock.id}`, {
        state: { ...navigationContext, stockIndex: currentIndex + 1 }
      });
    }
  };

  const goBackToResults = () => {
    // Return the Screener filters/sort the user was browsing with so the
    // results page shows the same filtered set, not a fresh default view.
    const screenerNav = parseScreenerNavState(navigationContext?.screener);
    navigate('/screener', {
      state: screenerNav ? { screener: screenerNav } : {}
    });
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'ArrowLeft') goToPrevious();
      else if (event.key === 'ArrowRight') goToNext();
      else if (event.key === 'Escape') goBackToResults();
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, filteredStocks]);

  useEffect(() => {
    setImageError(false);
  }, [currentStock?.id, currentStock?.short_fname, currentStock?.chart_generated_at, currentStock?.chart_file_sha256, currentStock?.chart_run_id, currentStock?.long_fname]);

  if (loading) return <LoadingIndicator message="Loading stock details..." />;

  if (!currentStock) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Stock Not Found</h2>
        <p className="text-gray-600 mb-6">The requested stock could not be found.</p>
        <button onClick={goBackToResults} className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700">
          Back to Search Results
        </button>
      </div>
    );
  }

  // currentIndex is -1 when the stock is not in the navigation list (e.g.
  // entered by URL, or the list went stale after a data push): disable both.
  const inList = currentIndex >= 0;
  const hasPrevious = inList && currentIndex > 0 && filteredStocks.length > 0;
  const hasNext = inList && currentIndex < filteredStocks.length - 1 && filteredStocks.length > 0;

  // Helper to render a metric row
  const MetricRow = ({ label, dataKey, stock, colorFn }) => {
    const val = stock[dataKey];
    if (val === undefined || val === null) return null;
    const formatted = formatChartDetailValue(dataKey, val);
    const colorClass = colorFn ? colorFn(val) : '';
    return (
      <div className="flex justify-between">
        <span className="text-gray-600">{label}:</span>
        <span className={`font-medium ${colorClass}`}>{formatted}</span>
      </div>
    );
  };

  const slopeColor = (v) => v > 0 ? 'text-green-600' : 'text-red-600';
  const slopeLabel = (v) => v > 0 ? '↑ Positive' : '↓ Negative';

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Navigation Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={goBackToResults} className="flex items-center text-blue-600 hover:text-blue-800 font-medium">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Search Results
        </button>
        
        <div className="flex items-center space-x-4">
          <button onClick={goToPrevious} disabled={!hasPrevious}
            className={`flex items-center px-4 py-2 rounded-md font-medium ${hasPrevious ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-gray-50 text-gray-400 cursor-not-allowed'}`}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          {filteredStocks.length > 0 && inList && (
            <span className="text-sm text-gray-500" data-testid="chart-detail-position">{currentIndex + 1} of {filteredStocks.length}</span>
          )}
          <button onClick={goToNext} disabled={!hasNext}
            className={`flex items-center px-4 py-2 rounded-md font-medium ${hasNext ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-gray-50 text-gray-400 cursor-not-allowed'}`}>
            Next
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Stock Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{currentStock.symbol}</h1>
            <p className="text-lg text-gray-600 mt-1">{currentStock.companyName || currentStock.company_name}</p>
            <div className="flex items-center mt-2 space-x-4">
              <span className="text-sm text-gray-500">{currentStock.sector}</span>
              <span className="text-sm text-gray-500">•</span>
              <span className="text-sm text-gray-500">{currentStock.industry}</span>
            </div>
          </div>
          <div className="mt-4 md:mt-0 text-right">
            <div className={`text-2xl font-bold ${
              currentStock.predicted_direction === 'up' || currentStock.predicted_direction === '↑' ? 'text-green-600' : 'text-red-600'
            }`}>
              {currentStock.predicted_direction === 'up' || currentStock.predicted_direction === '↑' ? '▲' : '▼'} {currentStock.predicted_direction}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Signal: {currentStock.signal_score}/5 → {currentStock.predicted_signal_score}/5
            </div>
          </div>
        </div>
      </div>

      {/* Chart Image */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Chart</h2>
        <div className="relative w-full" style={{ minHeight: '400px' }}>
          {imageError || !getImageUrl() ? (
            <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
              <div className="text-center text-gray-500">
                <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-lg font-medium">Chart image not available</p>
              </div>
            </div>
          ) : (
            <img src={getImageUrl()} alt={`${currentStock.symbol} chart`}
              className="w-full h-auto rounded-lg shadow-sm" referrerPolicy="no-referrer" onError={handleImageError}
              style={{ maxHeight: '600px', objectFit: 'contain' }} />
          )}
        </div>
      </div>

      <div className="mb-6">
        <FinancialHistoryCharts stock={currentStock} />
      </div>

      {/* Stock Data Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Information */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Trend Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between"><span className="text-gray-600">Trend State:</span><span className={`font-medium ${parseInt(currentStock.trend_state) === 1 ? 'text-green-600' : parseInt(currentStock.trend_state) === 0 ? 'text-red-600' : ''}`}>{parseInt(currentStock.trend_state) === 1 ? 'UpTrend' : parseInt(currentStock.trend_state) === 0 ? 'DownTrend' : 'N/A'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Days Since Crossover:</span><span className={`font-medium ${Number(currentStock.n_days_since_cross_over) < 0 ? 'text-red-600' : Number(currentStock.n_days_since_cross_over) > 0 ? 'text-green-600' : ''}`}>{currentStock.n_days_since_cross_over}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Trend Comment:</span><span className="font-medium">{currentStock.trend_comment}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Signal Score:</span><span className="font-medium">{currentStock.signal_score}/5</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Predicted Score:</span><span className="font-medium">{currentStock.predicted_signal_score}/5</span></div>
            <MetricRow label="% away from 6M high" dataKey="pcnt_upside_in_range_6m" stock={currentStock} />
            <MetricRow label="Is ETF" dataKey="is_etf" stock={currentStock} colorFn={() => ''} />
          </div>
        </div>

        {/* Technical Indicators */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Technical Indicators</h3>
          <div className="space-y-3">
            <MetricRow
              label="RSI"
              dataKey="rsi"
              stock={currentStock}
              colorFn={(v) => (v <= 30 ? 'text-red-600' : v >= 70 ? 'text-green-600' : '')}
            />
            {/* Removed SMA 10/200, SMA 30/200, SMA 50/200 per UI cleanup */}
            {currentStock.sma50_slope != null && (
              <div className="flex justify-between"><span className="text-gray-600">SMA50 Slope:</span><span className={`font-medium ${slopeColor(currentStock.sma50_slope)}`}>{slopeLabel(currentStock.sma50_slope)}</span></div>
            )}
            {currentStock.sma200_slope != null && (
              <div className="flex justify-between"><span className="text-gray-600">SMA200 Slope:</span><span className={`font-medium ${slopeColor(currentStock.sma200_slope)}`}>{slopeLabel(currentStock.sma200_slope)}</span></div>
            )}
            <MetricRow label="% from SMA200" dataKey="pcnt_from_sma200" stock={currentStock} />
            <MetricRow label="Z-Score 200d" dataKey="z_score_200d" stock={currentStock} />
            <MetricRow label="Z-Score 60d" dataKey="z_score_60d" stock={currentStock} />
            <MetricRow label="Z-Score 21d" dataKey="z_score_21d" stock={currentStock} />
          </div>
        </div>

        {/* Financial Metrics */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Metrics</h3>
          <div className="space-y-3">
            {/* ROE: good ≥15%, bad <5% */}
            <MetricRow label="Return on Equity" dataKey="returnOnEquityTTM" stock={currentStock}
              colorFn={(v) => v >= 0.15 ? 'text-green-600' : v < 0.05 ? 'text-red-600' : ''} />
            {/* P/S: good ≤2, bad >8 */}
            <MetricRow label="P/S Ratio" dataKey="priceSalesRatioTTM" stock={currentStock}
              colorFn={(v) => v <= 2 ? 'text-green-600' : v > 8 ? 'text-red-600' : ''} />
            {/* P/E: good ≤15, bad >40 or negative */}
            <MetricRow label="P/E Ratio" dataKey="priceEarningsRatioTTM" stock={currentStock}
              colorFn={(v) => (v > 0 && v <= 15) ? 'text-green-600' : (v < 0 || v > 40) ? 'text-red-600' : ''} />
            {/* PEG: good 0 to ≤1, bad <0 or >2 */}
            <MetricRow label="PEG Ratio" dataKey="priceEarningsToGrowthRatioTTM" stock={currentStock}
              colorFn={pegRatioColorClass} />
            {/* Gross margin: good ≥40%, bad <20% */}
            <MetricRow label="Gross Profit Margin" dataKey="grossProfitMarginTTM" stock={currentStock}
              colorFn={(v) => v >= 0.40 ? 'text-green-600' : v < 0.20 ? 'text-red-600' : ''} />
            {/* Net margin: good ≥15%, bad <0% */}
            <MetricRow label="Net Profit Margin" dataKey="netProfitMarginTTM" stock={currentStock}
              colorFn={(v) => v >= 0.15 ? 'text-green-600' : v < 0 ? 'text-red-600' : ''} />
            {/* Debt ratio: good ≤30%, bad >60% */}
            <MetricRow label="Debt Ratio" dataKey="debtRatioTTM" stock={currentStock}
              colorFn={(v) => v <= 0.30 ? 'text-green-600' : v > 0.60 ? 'text-red-600' : ''} />
            {/* Piotroski: good ≥7, bad ≤2 */}
            <MetricRow label="Piotroski Score (1-9)" dataKey="piotroskiScore" stock={currentStock}
              colorFn={(v) => v >= 7 ? 'text-green-600' : v <= 2 ? 'text-red-600' : ''} />
            {/* Revenue CAGR: good ≥15%, bad <0% */}
            <MetricRow label="Revenue CAGR" dataKey="revenue_cagr" stock={currentStock}
              colorFn={(v) => v >= 0.15 ? 'text-green-600' : v < 0 ? 'text-red-600' : ''} />
            {/* Analyst rating: green=buy, red=sell */}
            <MetricRow label="Analyst Rating" dataKey="ratingRecommendation" stock={currentStock}
              colorFn={(v) => /buy/i.test(String(v)) ? 'text-green-600' : /sell/i.test(String(v)) ? 'text-red-600' : ''} />
          </div>
        </div>

        {/* Growth & Valuation Metrics */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Growth & Valuation</h3>
          <div className="space-y-3">
            {/* Revenue growth: good ≥15%, bad <0% */}
            <MetricRow label="Revenue 1Y Growth" dataKey="revenue_1yr_growth" stock={currentStock}
              colorFn={(v) => v >= 0.15 ? 'text-green-600' : v < 0 ? 'text-red-600' : ''} />
            <MetricRow label="Revenue 3Y Growth" dataKey="revenue_3yr_growth" stock={currentStock}
              colorFn={(v) => v >= 0.15 ? 'text-green-600' : v < 0 ? 'text-red-600' : ''} />
            {/* Gross profit growth: good ≥10%, bad <0% */}
            <MetricRow label="Gross Profit 1Y Growth" dataKey="grossProfit_1yr_growth" stock={currentStock}
              colorFn={(v) => v >= 0.10 ? 'text-green-600' : v < 0 ? 'text-red-600' : ''} />
            <MetricRow label="Gross Profit 3Y Growth" dataKey="grossProfit_3yr_growth" stock={currentStock}
              colorFn={(v) => v >= 0.10 ? 'text-green-600' : v < 0 ? 'text-red-600' : ''} />
            {/* Forecast revenue growth: good ≥10%, bad <0% */}
            <MetricRow label="Forecast Revenue Growth" dataKey="forecast_next_year_revenue_growth_rate" stock={currentStock}
              colorFn={(v) => v >= 0.10 ? 'text-green-600' : v < 0 ? 'text-red-600' : ''} />
            {/* Forecast profit growth: good ≥10%, bad <0% */}
            <MetricRow label="Forecast Profit Growth" dataKey="forecast_next_year_profit_growth_rate" stock={currentStock}
              colorFn={(v) => v >= 0.10 ? 'text-green-600' : v < 0 ? 'text-red-600' : ''} />
            {/* EPS CAGR: good ≥15%, bad <0% */}
            <MetricRow label="EPS CAGR" dataKey="eps_cagr" stock={currentStock}
              colorFn={(v) => v >= 0.15 ? 'text-green-600' : v < 0 ? 'text-red-600' : ''} />
            {/* DCF: negative values are unfavorable */}
            <MetricRow label="DCF Valuation" dataKey="dcf" stock={currentStock}
              colorFn={dcfValuationColorClass} />
            {/* Price to DCF: green when below intrinsic value; negative values display as 0 */}
            <MetricRow label="Price to DCF Ratio" dataKey="price_to_dcf_ratio" stock={currentStock}
              colorFn={priceToDcfRatioColorClass} />
            {/* Rule of 40: green ≥40, black 1–39 */}
            <MetricRow label="Rule of 40" dataKey="rule_of_40" stock={currentStock}
              colorFn={(v) => Math.round(v * 100) >= 40 ? 'text-green-600' : ''} />
            {currentStock.holdings != null && currentStock.holdings !== '' && (
              <div>
                <div className="text-gray-600 mb-2">Top Holdings:</div>
                <div className="flex flex-wrap gap-2">
                  {String(currentStock.holdings)
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean)
                    .slice(0, 25)
                    .map((sym) => (
                      <HoldingChip key={sym} symbol={sym} />
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="flex justify-center mt-8 space-x-4">
        <button onClick={goToPrevious} disabled={!hasPrevious}
          className={`flex items-center px-6 py-3 rounded-md font-medium ${hasPrevious ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous Chart
        </button>
        <button onClick={goToNext} disabled={!hasNext}
          className={`flex items-center px-6 py-3 rounded-md font-medium ${hasNext ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
          Next Chart
          <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChartDetailView;
